import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { usePoints } from '../context/PointsContext';
import { API_URL } from '../services/api';
import { NairaBagText } from '../components/NairaBagText';
import { useAuth } from '../context/AuthContext';
import './Cart.css';

interface VariationSize {
  ukSize: number;
  footLengthCm: number;
  stockQuantity: number;
}

interface Variation {
  color: string;
  colorCode: string;
  images: string[];
  additionalPrice: number;
  isDefault?: boolean;
  sizes: VariationSize[];
}

interface ProductStock {
  _id: string;
  name: string;
  stockQuantity: number;
  ukShoeSizes?: { ukSize: number; stockQuantity: number }[];
  variations?: Variation[];
}

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, totalItems } = useCart();
  const { points, pointsToCurrency, forceRefresh } = usePoints();
  const { isAuthenticated } = useAuth();
  const [products, setProducts] = useState<ProductStock[]>([]);
  const [stockLoading, setStockLoading] = useState(true);
  
  // State for selected items
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  
  // State for points usage preference
  const [applyPoints, setApplyPoints] = useState(true);

  useEffect(() => {
    forceRefresh();
  }, [forceRefresh]);

  // Also refresh when returning to the page (visibility change)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        forceRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [forceRefresh]);

    // Fetch stock only for unique products currently in the cart.
  const cartProductIds = [...new Set(
    cart.map(item => item.productId).filter(Boolean)
  )].sort().join(',');

  useEffect(() => {
    const controller = new AbortController();

    const fetchCartStock = async () => {
      const productIds = cartProductIds
        ? cartProductIds.split(',')
        : [];

      if (productIds.length === 0) {
        setProducts([]);
        setStockLoading(false);
        return;
      }

      try {
        setStockLoading(true);

        const response = await fetch(`${API_URL}/products/stock/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ productIds }),
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch cart stock: ${response.status}`);
        }

        const data = await response.json();

        if (!Array.isArray(data.products)) {
          throw new Error('Invalid cart stock response.');
        }

        setProducts(data.products);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        console.error('Failed to fetch cart stock:', error);
        setProducts([]);
      } finally {
        if (!controller.signal.aborted) {
          setStockLoading(false);
        }
      }
    };

    fetchCartStock();

    return () => controller.abort();
  }, [cartProductIds]);
// Get current stock for a cart item - looks at variations with sizes
  const getCurrentStock = (item: any): number => {
    const product = products.find(p => p._id === item.productId);
    if (!product) return 0;
    
    // For shoes with color and size variations (NEW STRUCTURE)
    if (item.selectedColor && item.selectedSize && product.variations) {
      const variation = product.variations.find(v => v.color === item.selectedColor);
      if (variation && variation.sizes) {
        // Extract UK size number from string like "UK 42"
        const sizeMatch = item.selectedSize.match(/UK (\d+)/);
        if (sizeMatch) {
          const ukSize = parseInt(sizeMatch[1]);
          const sizeStock = variation.sizes.find(s => s.ukSize === ukSize);
          return sizeStock?.stockQuantity || 0;
        }
      }
    }
    
    // For shoes with only sizes (old structure - backward compatibility)
    if (item.selectedSize && product.ukShoeSizes) {
      const sizeMatch = item.selectedSize.match(/UK (\d+)/);
      if (sizeMatch) {
        const sizeNum = parseInt(sizeMatch[1]);
        const sizeStock = product.ukShoeSizes.find(s => s.ukSize === sizeNum);
        return sizeStock?.stockQuantity || 0;
      }
    }
    
    // For items with color variations only (no sizes)
    if (item.selectedColor && product.variations) {
      const variation = product.variations.find(v => v.color === item.selectedColor);
      // Check if variation has sizes array (shoes) or direct stockQuantity
      if (variation) {
        if (variation.sizes && variation.sizes.length > 0) {
          // Sum all sizes stock for color-only display
          return variation.sizes.reduce((sum, s) => sum + s.stockQuantity, 0);
        }
        // For color-only products (no sizes)
        return (variation as any).stockQuantity || 0;
      }
    }
    
    // For simple products
    return product.stockQuantity || 0;
  };

  // Function to get full image URL
  const getFullImageUrl = (imagePath: string): string => {
    if (!imagePath) return '/placeholder.png';
    if (imagePath.startsWith('http')) return imagePath;
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    return `${API_URL.replace('/api', '')}/${cleanPath}`;
  };

  // Toggle item selection
  const toggleItemSelection = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Select all items
  const toggleSelectAll = () => {
    if (selectedItems.size === cart.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(cart.map(item => item.id)));
    }
  };

  // Calculate totals for selected items only
  const selectedTotalPrice = cart
    .filter(item => selectedItems.has(item.id))
    .reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const selectedTotalItems = cart
    .filter(item => selectedItems.has(item.id))
    .reduce((sum, item) => sum + item.quantity, 0);

  const pointsValue = pointsToCurrency(points);
  
  // Only apply points if user has chosen to use them
  const maxRedeemable = applyPoints ? Math.min(pointsValue, selectedTotalPrice) : 0;
  const discountedTotal = selectedTotalPrice - maxRedeemable;
  
  // Points earned ONLY on the actual amount paid
  const actualAmountPaid = discountedTotal;
  const pointsEarned = Math.floor(actualAmountPaid / 1000);
  
  // Points spent (using existing points for discount)
  const pointsSpent = Math.floor(maxRedeemable * 10);
  
  // Points if saving for later
  const pointsIfSaving = Math.floor(selectedTotalPrice / 100) + points;
  
  // Validate stock before updating quantity
  const validateAndUpdateQuantity = async (
    item: any,
    newQuantity: number
  ) => {
    if (stockLoading) {
      alert('Please wait while we verify product availability.');
      return false;
    }

    const currentStock = getCurrentStock(item);

    if (newQuantity > currentStock) {
      alert(
        `Sorry, only ${currentStock} ${
          currentStock === 1 ? 'unit is' : 'units are'
        } available for "${item.name}".`
      );
      return false;
    }

    if (newQuantity < 1) {
      return false;
    }

    updateQuantity(item.id, newQuantity);
    return true;
  };
  
  const handleQuantityChange = (item: any, delta: number) => {
    const newQuantity = item.quantity + delta;
    validateAndUpdateQuantity(item, newQuantity);
  };
   
  const handleCheckout = () => {
    if (stockLoading) {
      alert('Please wait while we verify product availability.');
      return;
    }

    if (selectedItems.size === 0) {
      alert('Please select at least one item to checkout');
      return;
    }

    // Validate stock before proceeding
    for (const item of cart) {
      if (selectedItems.has(item.id)) {
        const currentStock = getCurrentStock(item);

        if (item.quantity > currentStock) {
          alert(
            `"${item.name}" - Only ${currentStock} ${
              currentStock === 1 ? 'unit is' : 'units are'
            } available. Please adjust your quantity.`
          );
          return;
        }

        if (currentStock === 0) {
          alert(`"${item.name}" is sold out.`);
          return;
        }
      }
    }

    // Get the exact items the customer selected
    const selectedCartItems = cart.filter(item =>
      selectedItems.has(item.id)
    );

    const pointsToSend = applyPoints ? pointsSpent : 0;

    // Preserve everything needed to reconstruct checkout
    const checkoutState = {
      selectedItems: selectedCartItems,
      selectedTotal: selectedTotalPrice,
      pointsToRedeem: pointsToSend,
      applyPoints: applyPoints
    };

    // ---------------------------------------------------------
    // LOGGED-IN CUSTOMER
    // ---------------------------------------------------------
    if (isAuthenticated) {
      navigate('/checkout', {
        state: checkoutState
      });

      return;
    }

    // ---------------------------------------------------------
    // GUEST CUSTOMER
    // ---------------------------------------------------------
    // Send guest to account creation first, while preserving
    // the selected cart/checkout information.
    navigate('/register', {
      state: {
        returnTo: '/checkout',
        checkoutState
      }
    });
  };

  if (cart.length === 0) {
    return (
      <div className="cart-empty">
        <h2>Your Cart is Empty</h2>
        <p>Start shopping to add items to your cart!</p>
        <button onClick={() => navigate('/')}>Continue Shopping</button>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <h2>Shopping Cart ({totalItems} items)</h2>
      
      <div className="cart-items">
        <div className="cart-header-row">
          <div className="cart-header-select">
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={selectedItems.size === cart.length && cart.length > 0}
                onChange={toggleSelectAll}
                className="select-all-checkbox"
              />
              <span>Select All</span>
            </label>
          </div>
          <div className="cart-header-image">Image</div>
          <div className="cart-header-details">Product</div>
          <div className="cart-header-actions">Quantity</div>
          <div className="cart-header-total">Total</div>
        </div>

        {cart.map(item => {
          const currentStock = getCurrentStock(item);

          // Do not interpret an unloaded product as out of stock.
          const isStockKnown = !stockLoading;

          const isLowStock =
            isStockKnown &&
            currentStock > 0 &&
            currentStock < 5;

          const isOutOfStock =
            isStockKnown &&
            currentStock === 0;
          
          return (
            <div key={item.id} className={`cart-item ${isOutOfStock ? 'out-of-stock-item' : ''}`}>
              <div className="cart-item-select">
                <input
                  type="checkbox"
                  checked={selectedItems.has(item.id)}
                  onChange={() => toggleItemSelection(item.id)}
                  disabled={isOutOfStock}
                  className="item-checkbox"
                />
              </div>
              <img 
                src={getFullImageUrl(item.image)} 
                alt={item.name}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder.png';
                }}
              />
              <div className="cart-item-details">
                <h4>{item.name}</h4>
                {item.selectedColor && (
                  <div className="item-variant">
                    <span className="variant-label">Color:</span> {item.selectedColor}
                  </div>
                )}
                {item.selectedSize && (
                  <div className="item-variant">
                    <span className="variant-label">Size:</span> {item.selectedSize}
                  </div>
                )}
                {item.originalPrice && item.originalPrice > item.price ? (
                  <div className="price-container">
                    <span className="original-price">₦{item.originalPrice.toLocaleString()}</span>
                    <span className="discounted-price">₦{item.price.toLocaleString()}</span>
                    {item.discount && <span className="discount-badge">-{item.discount}%</span>}
                  </div>
                ) : (
                  <p>₦{item.price.toLocaleString()}</p>
                )}
                {isLowStock && !isOutOfStock && (
                  <div className="stock-warning low-stock">
                    Only {currentStock} left in stock!
                  </div>
                )}
                {isOutOfStock && (
                  <div className="stock-warning out-of-stock">
                    Out of stock - Please remove from cart
                  </div>
                )}
              </div>
              <div className="cart-item-actions">
                <button 
                  onClick={() => handleQuantityChange(item, -1)}
                  disabled={item.quantity <= 1 || isOutOfStock}
                >
                  -
                </button>
                <span>{item.quantity}</span>
                <button 
                  onClick={() => handleQuantityChange(item, 1)}
                  disabled={item.quantity >= currentStock || isOutOfStock}
                >
                  +
                </button>
                <button 
                  onClick={() => removeFromCart(item.id)} 
                  className="remove-item-btn"
                >
                  🗑️
                </button>
              </div>
              <div className="cart-item-total">
                ₦{(item.price * item.quantity).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      <div className="cart-summary">
        <div className="selection-summary">
          <p>
            Selected: {selectedTotalItems} item{selectedTotalItems !== 1 ? 's' : ''} 
            (out of {totalItems} total)
          </p>
        </div>

        {selectedTotalPrice > 0 && points > 0 && (
          <div className="points-options-section">
            <h3>
              <NairaBagText text="[NAIRA_BAG] Reward Points Options" size="lg" />
            </h3>
            <div className="points-balance">
              <NairaBagText text="[NAIRA_BAG:md] ✨ Points Balance:" />
              <span className="points-value">{points.toLocaleString()} points</span>
              <span className="points-worth">(worth ₦{pointsValue.toLocaleString()})</span>
            </div>
            
            <div className="points-toggle">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={applyPoints}
                  onChange={(e) => setApplyPoints(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <div className="toggle-label">
                <strong>{applyPoints ? 'Use my points' : 'Save my points'}</strong>
              </div>
            </div>

            {applyPoints && maxRedeemable > 0 && (
              <div className="points-impact negative">
                <div className="impact-neutral">
                  <NairaBagText text={`[NAIRA_BAG] You'll spend ${pointsSpent.toLocaleString()} points from your balance`} size="sm" />
                </div>  
              </div>
            )}

            {!applyPoints && pointsIfSaving > 0 && (
              <div className="points-impact positive">
                <div className="impact-neutral">
                  <NairaBagText text={`[NAIRA_BAG] Keep your ${points.toLocaleString()} points for a future purchase`} size="sm" />
                </div>
              </div>
            )}

            {!applyPoints && pointsIfSaving === 0 && (
              <div className="points-impact neutral">
                <div className="impact-neutral">
                  <span>💡 Add ₦100 or more to earn points on this order</span>
                </div>
              </div>
            )}

            <div className="points-tip">
              💡 <strong>Pro Tip:</strong> {applyPoints 
                ? "Using points gives you instant savings! You'll earn points only on what you actually pay."
                : "Saving your points lets you accumulate more for bigger purchases. Great for buying gifts!"}
            </div>
          </div>
        )}

        <div className="summary-details">
          <div>Subtotal (selected items): <span>₦{selectedTotalPrice.toLocaleString()}</span></div>
          {applyPoints && maxRedeemable > 0 && (
            <div className="discount-row">Points Discount: <span>-₦{maxRedeemable.toLocaleString()}</span></div>
          )}
          <div className="total">Total to Pay: <span>₦{discountedTotal.toLocaleString()}</span></div>
          {!applyPoints && pointsEarned > 0 && (
            <div className="points-earn-notice">
              + {pointsEarned.toLocaleString()} points will be earned after payment
            </div>
          )}
          {applyPoints && pointsSpent > 0 && (
            <div className="points-spend-notice">
              - {pointsSpent.toLocaleString()} points deduction
            </div>
          )}
          {applyPoints && pointsEarned > 0 && (
            <div className="points-earn-notice">
              + {pointsEarned.toLocaleString()} points gained
            </div>
          )}
        </div>

        <button 
          className={`checkout-btn ${selectedItems.size === 0 ? 'disabled' : ''}`} 
          onClick={handleCheckout}
          disabled={selectedItems.size === 0}
        >
          {applyPoints && maxRedeemable > 0
            ? `Pay ₦${discountedTotal.toLocaleString()}`
            : !applyPoints && pointsIfSaving > 0
              ? `Pay ₦${selectedTotalPrice.toLocaleString()}`
              : `Checkout (${selectedTotalItems} items)`}
        </button>
      </div>
    </div>
  );
};

export default Cart;